import type { UiEventBus, UiEventName } from './uiEventBus';

export type KeyBinding = {
  readonly key: string;
  readonly ctrl?: boolean;
  readonly alt?: boolean;
  readonly shift?: boolean;
  readonly meta?: boolean;
  readonly event: UiEventName;
};

type ListenerTarget = Pick<Document, 'addEventListener' | 'removeEventListener'>;

const TYPING_SELECTOR = 'input, textarea, select, [contenteditable="true"], [role="dialog"]';

const isTyping = (target: EventTarget | null): boolean =>
  target instanceof Element && target.closest(TYPING_SELECTOR) !== null;

const matches = (binding: KeyBinding, event: KeyboardEvent): boolean =>
  (binding.key.length === 1 && !binding.shift
    ? binding.key.toLowerCase() === event.key.toLowerCase()
    : binding.key === event.key) &&
  (binding.ctrl ?? false) === event.ctrlKey &&
  (binding.alt ?? false) === event.altKey &&
  (binding.shift ?? false) === event.shiftKey &&
  (binding.meta ?? false) === event.metaKey;

export function installKeyboardBridge(
  bus: UiEventBus,
  bindings: readonly KeyBinding[],
  target: ListenerTarget = document
): () => void {
  const onKeyDown = (event: Event): void => {
    if (!(event instanceof KeyboardEvent) || bindings.length === 0) return;
    if (isTyping(event.target)) return;
    const binding = bindings.find((candidate) => matches(candidate, event));
    if (!binding) return;
    if (binding.ctrl || binding.alt || binding.meta || binding.key.length !== 1) event.preventDefault();
    if (event.repeat) return;
    bus.emit(binding.event, { source: 'keyboard', key: event.key });
  };
  target.addEventListener('keydown', onKeyDown);
  return () => target.removeEventListener('keydown', onKeyDown);
}
